import { Button, Checkbox, Dialog, Input } from '@artosera/ui';
import { PLAFOND_LOT_OEUVRES } from '@artosera/shared';
import type { JSX } from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useOeuvres } from '../../api/oeuvres.js';
import { LotOeuvresDialog } from '../oeuvres/lot/LotOeuvresDialog.js';
import { useDebounce } from '../oeuvres/useDebounce.js';
import styles from './CreationStand.module.css';

const PAS = 30;

/**
 * « Ajouter des œuvres » au stand : on cherche et on coche les œuvres, puis le
 * lot de localisation prend le relais (aperçu, confirmation, mouvement tracé
 * au journal). Rien n'est écrit ici : la localisation ne change que par le lot.
 */
export function AjouterOeuvresStandDialog({
  galerieSlug,
  stand,
  onFerme,
}: {
  galerieSlug: string;
  /** Le stand : un lieu de type `foire`. */
  stand: { id: string; nom: string };
  onFerme: () => void;
}): JSX.Element {
  const { t } = useTranslation(['regie', 'commun']);
  const [recherche, setRecherche] = useState('');
  const rechercheDebattue = useDebounce(recherche, 300);
  const [choisies, setChoisies] = useState<readonly string[]>([]);
  const [lot, setLot] = useState(false);
  const [parPage, setParPage] = useState(PAS);

  const resultats = useOeuvres(galerieSlug, {
    ...(rechercheDebattue.trim() ? { q: rechercheDebattue.trim() } : {}),
    parPage,
    tri: 'recent',
  });
  const reste = (resultats.data?.pagination.total ?? 0) > parPage;
  // Les œuvres déjà au stand n'ont rien à y faire de plus.
  const proposees = (resultats.data?.oeuvres ?? []).filter(
    (oeuvre) => oeuvre.localisationId !== stand.id,
  );
  const plein = choisies.length >= PLAFOND_LOT_OEUVRES;

  const basculer = (id: string, coche: boolean): void =>
    setChoisies((avant) => (coche ? [...avant, id] : avant.filter((autre) => autre !== id)));

  if (lot) {
    return (
      <LotOeuvresDialog
        type="localisation"
        oeuvreIds={choisies}
        parametresInitiaux={{ lieu: stand.id }}
        onAnnuler={() => setLot(false)}
        onTermine={(refusees) => {
          // « Garder les refusées sélectionnées » : on revient au choix avec elles.
          if (refusees !== null && refusees.length > 0) {
            setChoisies(refusees);
            setLot(false);
          } else onFerme();
        }}
      />
    );
  }

  return (
    <Dialog
      open
      onOpenChange={(ouvert) => {
        if (!ouvert) onFerme();
      }}
      title={t('regie:foire.ajout.titre')}
      description={t('regie:foire.ajout.description', { stand: stand.nom })}
      closeLabel={t('commun:actions.fermer')}
      footer={
        <>
          <Button variant="ghost" onClick={onFerme}>
            {t('commun:actions.annuler')}
          </Button>
          <Button disabled={choisies.length === 0} onClick={() => setLot(true)}>
            {t('regie:foire.ajout.continuer', { count: choisies.length })}
          </Button>
        </>
      }
    >
      <div className={styles.recherche}>
        <Input
          type="search"
          placeholder={t('regie:foire.ajout.recherche')}
          aria-label={t('regie:foire.ajout.recherche')}
          value={recherche}
          onChange={(event) => setRecherche(event.target.value)}
        />
      </div>
      <div className={styles.resultats}>
        {resultats.isLoading ? (
          <p className={styles.vide}>{t('commun:chargement')}</p>
        ) : resultats.isError ? (
          <p className={styles.vide} role="alert">
            {t('regie:foire.ajout.erreur')}{' '}
            <Button variant="ghost" onClick={() => void resultats.refetch()}>
              {t('regie:foire.ajout.reessayer')}
            </Button>
          </p>
        ) : proposees.length === 0 && !reste ? (
          <p className={styles.vide}>{t('regie:foire.ajout.aucune')}</p>
        ) : (
          proposees.map((oeuvre) => {
            const coche = choisies.includes(oeuvre.id);
            return (
              <div key={oeuvre.id} className={styles.resultat}>
                <Checkbox
                  id={`ajout-stand-${oeuvre.id}`}
                  checked={coche}
                  disabled={!coche && plein}
                  onCheckedChange={(valeur) => basculer(oeuvre.id, valeur === true)}
                  label={
                    <span className={styles.resultatTexte}>
                      <span>
                        {oeuvre.artisteNom} — {oeuvre.titre}
                      </span>
                      <span className={styles.resultatMeta}>{oeuvre.numeroInventaire}</span>
                    </span>
                  }
                />
              </div>
            );
          })
        )}
        {reste && !resultats.isError ? (
          <Button variant="ghost" onClick={() => setParPage((avant) => avant + PAS)}>
            {t('regie:foire.ajout.plus')}
          </Button>
        ) : null}
      </div>
      <p className={styles.choisies} role="status">
        {t('regie:foire.ajout.choisies', { count: choisies.length })}
      </p>
      {plein ? (
        <p className={styles.choisies}>
          {t('regie:foire.ajout.plafond', { plafond: PLAFOND_LOT_OEUVRES })}
        </p>
      ) : null}
    </Dialog>
  );
}
